import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { once } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DateRangeInterface, DSSetup, Service } from 'webapp-types';
import { PopupEvent, Unit } from 'webapp/enums/enums';

import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormMetricSelectComponent from 'webapp/components/form/metric-select/index';
import FormSelectAttributesComponent from 'webapp/components/form/select-attributes';
import FormSelectDimensionsComponent from 'webapp/components/form/select-dimensions';
import FormSelectUnitsComponent from 'webapp/components/form/select-units';
import RowErrorComponent from 'webapp/components/row-error';

import type { MetricSetup } from 'webapp/models-custom';
import type DataSelect from 'webapp/models-custom/data-select';
import type DateRange from 'webapp/models-custom/date-range';
import type DsCompare from 'webapp/models-custom/ds-compare';

interface Args {
	model: DataSelect;
	submitInProgress: boolean;
	scorecardDateRange: DateRange;
	scorecardCompare: DsCompare;
	onDelete: (ds: DataSelect) => void;
}

export default class ScorecardMetricsRowComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/scorecards')
	declare mmScorecardsService: Service.ModelManager.Scorecards;
	@service('model-manager/data-selects')
	declare mmDataSelectsService: Service.ModelManager.DataSelects;

	// cached to check an actual change
	_scorecardDateRangeFormula: DateRangeInterface.FormulaInterface;
	_scorecardCompareFormula: DSSetup.Compare;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this._scorecardDateRangeFormula = this.args.scorecardDateRange.formula;
		this._scorecardCompareFormula = this.args.scorecardCompare.formula!;
	}

	get rowClass(): string {
		const countToText = ['', 'one', 'two', 'three', 'four'] as const;

		let visibleFieldsInRow = 1;

		if (this.metricSetup.allowDimensions) {
			visibleFieldsInRow++;
		}

		if (this.metricSetup.allowUnitNameFilter) {
			visibleFieldsInRow++;
		}

		if (this.metricSetup.allowBasicDimensionValueFilters) {
			visibleFieldsInRow++;
		}

		const index = Math.min(visibleFieldsInRow, countToText.length - 1);
		return `${countToText[index]}-shown`;
	}

	get allowUnits() {
		return this.metricSetup.allowUnitNameFilter;
	}

	get metricSetup(): MetricSetup {
		return this.args.model.metricSetup;
	}

	get dateRangeNotes(): string[] {
		const dataSelect = this.args.model;

		if (!dataSelect.metricSetup.isFilledSingleOutput) {
			return [];
		}

		const notes = [];
		let checkCompare = true;

		// note can say that the current date range on this ms is disabled (1st condition)
		// or that a different date range is chosen than the scorecard general one (2nd condition)
		if (dataSelect.dateRangeCM.isEqual(this.args.scorecardDateRange.formula)) {
			const limitation = dataSelect.limitations.limitationForSelectedRangeAndGranulation;
			if (limitation?.disabled) {
				notes.push(limitation.infoText);
				checkCompare = false;
			}
		} else {
			notes.push(`Date range "${dataSelect.dateRangeCM.rangeTitleUX}" selected.`);
		}

		// compare titles bcos of "Previous" which has diff formula
		const compare = dataSelect.limitations.compare!;
		if (checkCompare && compare.titleUX !== this.args.scorecardCompare.titleUX) {
			notes.push(`Compare "${compare.titleUX}" selected.`);
		}

		return notes;
	}

	get showUnitsAtMetricRow(): boolean {
		return this.allowUnits && !this.metricSetup.allowDimensions;
	}

	@action
	changeUnit(unit: string | null): void {
		const dataSelect = this.args.model;
		dataSelect.updateUnit(unit);
	}

	@action
	changeDimensions(dimensions: string[]): void {
		const dataSelect = this.args.model;
		dataSelect.updateDimensions(dimensions);
		dataSelect.updateUnit(Unit.EMPTY);
	}

	@action
	changeMetricData(metricData: DSSetup.MetricData): void {
		const dataSelect = this.args.model;
		dataSelect.updateMetricData(metricData);
		dataSelect.updateUnit(Unit.EMPTY);
	}

	@action
	changeAttribute(attribute: string | null): void {
		this.metricSetup.setFiltersFromAttributeName(attribute);
	}

	@action
	deleteRow(): void {
		this.args.onDelete(this.args.model);
	}

	@action
	openAdvanced(): void {
		const dataSelectCopy = this.mmDataSelectsService.createNewModel(this.args.model.toJSON()); // create from props to keep local changes

		// only for advanced
		dataSelectCopy.requireSingleOutput = true;

		const popup = this.mmDataSelectsService.openAdvancedPopup({
			dataSelect: dataSelectCopy,
			sourceForTracking: 'scorecard',
			saveFn: (ds) => {
				const model = this.args.model;
				if (model && ds.validate()) {
					this.mmScorecardsService.updateDataSelectFromAnother(model, ds);
					ds.applyBufferedChanges(ds.dirtyProperties); // argument to force instant apply instead of next runloop bcos of discard
				}
			},
		});

		popup.on(PopupEvent.CLOSED, this, () => {
			void dataSelectCopy.delete();
		});
	}

	@action
	updatePreviewData(): void {
		// trigger `schedulePreviewUpdate` observer in scorecard-preview-line-with-query
		// to recalculate preview value - https://app.asana.com/0/7582091858306/1199643722082633/f
		this.args.model.metricSetupKey = this.args.model.metricSetupKey;
	}

	@action
	scorecardDateRangeChanged(): void {
		// only overwrite ms when actually changed (and not when scorecard model is refreshed)
		if (!this.args.scorecardDateRange.isEqual(this._scorecardDateRangeFormula)) {
			this._scorecardDateRangeFormula = this.args.scorecardDateRange.formula;
			once(this, this.switchMetricDateRange);
		}
	}

	@action
	scorecardCompareChanged(): void {
		// only overwrite ms when actually changed (and not when scorecard model is refreshed)
		if (!this.args.scorecardCompare.isEqual(this._scorecardCompareFormula)) {
			this._scorecardCompareFormula = this.args.scorecardCompare.formula!;
			once(this, this.switchMetricCompare);
		}
	}

	private switchMetricDateRange() {
		const scorecardDateRange = this.args.scorecardDateRange,
			dataSelect = this.args.model;
		if (!dataSelect.dateRangeCM.isEqual(scorecardDateRange.formula)) {
			dataSelect.updateDateRange(scorecardDateRange);
		}
	}

	private switchMetricCompare() {
		if (this.args.scorecardDateRange.isAllTime) {
			return;
		}

		const ds = this.args.model,
			currentCompare = ds.limitations.compare!,
			newCompare = this.args.scorecardCompare.createCopy();

		// for updating to previous, we cant use formula bcos dateRange might differ
		if (newCompare.isPrevious) {
			if (!currentCompare.isPrevious && !ds.dateRangeCM.isSince) {
				ds.changeCompare(currentCompare, newCompare);
			}
		} else if (!currentCompare.isEqual(newCompare.formula)) {
			ds.changeCompare(currentCompare, newCompare);
		}
	}
	<template>
		<div
			class="row {{this.rowClass}}"
			{{didUpdate
				this.scorecardDateRangeChanged
				@scorecardDateRange.rangeFormula
				@scorecardDateRange.granulationFormula
			}}
			{{didUpdate
				this.scorecardCompareChanged
				@scorecardCompare.typeId
				@scorecardCompare.formula
			}}
		>

			{{#unless @submitInProgress}}
				<span class="hover">
					<a role="button" class="drag-handle">
						<CommonUiIconComponent @name="List" @size="sm" />
					</a>
					{{#if @model.metricSetupKey}}
						<a role="button" class="settings-icon" {{on "click" this.openAdvanced}}>
							<CommonUiIconComponent @name="Settings" @size="sm" />
						</a>
					{{/if}}
					<a role="button" class="icon" {{on "click" this.deleteRow}}>
						<CommonUiIconComponent @name="Trash" @size="sm" />
					</a>
				</span>
			{{/unless}}

			{{! class important for closing and attaching blue bubble }}
			<div class="metrickey">
				<FormMetricSelectComponent
					@selected={{@model.metricData}}
					@onChange={{this.changeMetricData}}
					@isDisabled={{@submitInProgress}}
					@onAfterMetricEdit={{this.updatePreviewData}}
				/>
				<RowErrorComponent @items={{@model.errors.metricSetupKey}} />
			</div>

			{{#if this.showUnitsAtMetricRow}}
				<div>
					<FormSelectUnitsComponent
						@selected={{this.metricSetup.unitName}}
						@metricSetupForQuery={{this.metricSetup}}
						@dateRangeForQuery={{@model.dateRangeCM}}
						@onChange={{this.changeUnit}}
						@isMultiple={{false}}
						@maxSelectionLength={{1}}
						@isDisabled={{@submitInProgress}}
						@allowAllUnits={{false}}
					/>
					<RowErrorComponent @items={{@model.errors.unit}} />
				</div>
			{{/if}}

			{{#if this.metricSetup.allowDimensions}}
				<div>
					<FormSelectDimensionsComponent
						@metricSetup={{this.metricSetup}}
						@onChange={{this.changeDimensions}}
						@isDisabled={{@submitInProgress}}
					/>
				</div>

				{{#if this.metricSetup.allowBasicDimensionValueFilters}}
					<div>
						<FormSelectAttributesComponent
							@selected={{this.metricSetup.firstAttributeNameFromBasicFilters}}
							@metricSetupForQuery={{this.metricSetup}}
							@dateRangeForQuery={{@model.dateRangeCM}}
							@onChange={{this.changeAttribute}}
							@isMultiple={{false}}
							@maxSelectionLength={{1}}
							@isDisabled={{@submitInProgress}}
						/>
						<RowErrorComponent @items={{@model.errors.attribute}} />
					</div>
				{{/if}}

				{{#if this.metricSetup.allowUnitNameFilter}}
					{{#unless this.showUnitsAtMetricRow}}
						<div>
							<FormSelectUnitsComponent
								@selected={{this.metricSetup.unitName}}
								@metricSetupForQuery={{this.metricSetup}}
								@dateRangeForQuery={{@model.dateRangeCM}}
								@onChange={{this.changeUnit}}
								@isMultiple={{false}}
								@maxSelectionLength={{1}}
								@isDisabled={{@submitInProgress}}
								@allowAllUnits={{false}}
							/>
							<RowErrorComponent @items={{@model.errors.unit}} />
						</div>
					{{/unless}}
				{{/if}}
			{{/if}}

			{{#each this.dateRangeNotes as |note|}}
				<span class="dateRangeNote">
					<CommonUiIconComponent @name="InfoFill" @size="sm" />
					{{note}}
				</span>
			{{/each}}
		</div>
	</template>
}
